/**
 * Set, change or remove one requirement's individual deadline.
 *
 * TWO DIFFERENT REQUESTS, TWO DIFFERENT BUTTONS. The endpoint reads the body
 * with `exclude_unset`, so an absent key means "leave the stored deadline
 * alone" and an explicit `null` means "remove it". Those are not the same
 * intention and they do not share a control: saving sends the date, removing
 * sends `{ "due_date_override": null }`. A single button that inferred which
 * one was meant from an empty field would send `{}` on the day someone typed
 * a date and deleted it again, and nothing would happen.
 *
 * "ENTFERNEN", NOT "LÖSCHEN". Removing the override does not remove the
 * deadline — the catalogue default applies again afterwards. Wording it as a
 * deletion would promise the requirement becomes open-ended, which is the
 * opposite of what happens.
 *
 * The dialog knows nothing about what the change will DO to the row. It sends
 * the decision and asks the page to reload; the status, the effective deadline
 * and the counts all come back from the read model that owns them.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { patchRequirementDueDate } from "../../../api/adminData";
import { formatDateOnly } from "../../../utils/datetime";
import { requirementTypeLabel } from "./requirementLabels";

/** One labelled read-only fact. */
function Fact({ label, children }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[12px] text-muted-foreground">{label}</span>
      <span className="text-[12.5px] font-medium text-foreground">{children}</span>
    </div>
  );
}

export default function RequirementDueDateModal({ row, onClose, onSaved }) {
  // `date` fields arrive as `YYYY-MM-DD` — already exactly what an
  // `<input type="date">` wants. Passing them through an instant→Zurich
  // conversion would be converting a civil date that was never an instant.
  const [value, setValue] = useState("");
  const [pending, setPending] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    setValue(row?.due_date_override || "");
    setSubmitError(null);
    setPending(false);
  }, [row]);

  if (!row) return null;

  const hasOverride = Boolean(row.due_date_override);

  async function send(payload, successMessage) {
    setPending(true);
    setSubmitError(null);
    try {
      await patchRequirementDueDate(row.tenancy_id, row.requirement_type, payload);
      toast.success(successMessage);
      onSaved();
    } catch (e) {
      const message = e?.message || "Die Frist konnte nicht gespeichert werden.";
      setSubmitError(message);
      toast.error(message);
      setPending(false);
    }
  }

  return (
    <AlertDialog open onOpenChange={(open) => (!open && !pending ? onClose() : null)}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>Frist ändern</AlertDialogTitle>
          <AlertDialogDescription>
            Eine individuelle Frist gilt nur für diesen Nachweis dieses
            Mietverhältnisses. Wird sie entfernt, gilt wieder die Standardfrist.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="grid gap-1.5 rounded-[8px] border border-border bg-muted/30 px-3 py-2.5">
          <Fact label="Nachweis">{requirementTypeLabel(row.requirement_type)}</Fact>
          <Fact label="Mieter">{row.tenant_name || "—"}</Fact>
          <Fact label="Mietverhältnis">{row.tenancy_reference_number || "—"}</Fact>
          <Fact label="Effektive Frist">
            {row.effective_due_date ? formatDateOnly(row.effective_due_date) : "Keine Frist"}
          </Fact>
          {row.default_due_date ? (
            <Fact label="Standardfrist">{formatDateOnly(row.default_due_date)}</Fact>
          ) : null}
          <Fact label="Individuelle Frist">
            {hasOverride ? formatDateOnly(row.due_date_override) : "Keine"}
          </Fact>
        </div>

        <div className="grid gap-1.5">
          <label
            htmlFor="requirement-due-date"
            className="text-[12px] font-medium text-muted-foreground"
          >
            Individuelle Frist
          </label>
          <input
            id="requirement-due-date"
            type="date"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={pending}
            className="w-full rounded-[8px] border border-input bg-background px-3 py-2 text-[13px] text-foreground outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
          />
          {submitError ? (
            <p
              role="alert"
              className="m-0 rounded-[8px] border border-destructive/30 bg-destructive/5 px-3 py-2 text-[12.5px] text-destructive"
            >
              {submitError}
            </p>
          ) : null}
        </div>

        <AlertDialogFooter className="gap-2">
          {hasOverride ? (
            <button
              type="button"
              disabled={pending}
              onClick={() =>
                send({ due_date_override: null }, "Individuelle Frist entfernt.")
              }
              className="inline-flex h-10 items-center justify-center rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:pointer-events-none disabled:opacity-50"
            >
              Individuelle Frist entfernen
            </button>
          ) : null}
          <button
            type="button"
            disabled={pending}
            onClick={onClose}
            className="inline-flex h-10 items-center justify-center rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:pointer-events-none disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="button"
            disabled={pending || !value}
            onClick={() => send({ due_date_override: value }, "Frist gespeichert.")}
            className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
          >
            {pending ? "Speichere …" : "Frist speichern"}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
